import type { MarketConfig } from '../core/internal/market-config-catalog.js';
import {
  computeMarketPipelineStage,
  type MarketPipelineStage,
  projectedResolutionGap,
  selectConvictionLeader,
} from './market-pipeline-stage.js';
import type { MarketResolutionThresholdState } from './resolution-threshold.js';
import {
  type MarketResolutionThresholdConsumers,
  marketResolutionThresholdConsumers,
} from './resolution-threshold-consumers.js';
import {
  type ProjectMarketResolutionAtResult,
  projectMarketResolutionAt,
  type ResolutionThresholdProjectionOutcome,
} from './resolution-threshold-projection.js';

export interface MarketResolutionViewInput {
  readonly status: string;
  readonly liveConviction: ReadonlyMap<number, number>;
  readonly resolutionThreshold: MarketResolutionThresholdState;
  readonly showGoalPercent?: boolean;
  readonly config: MarketConfig | undefined;
  readonly outcomeCount: number;
  readonly tradingStartedAt: string | null;
  readonly convictionUpdatedAt: string | undefined;
  readonly retentionPerSecondPpb: string | undefined;
  readonly outcomes: readonly ResolutionThresholdProjectionOutcome[] | undefined;
}

export interface MarketResolutionView {
  readonly consumers: MarketResolutionThresholdConsumers;
  readonly pipelineStage: MarketPipelineStage;
  readonly projected: ProjectMarketResolutionAtResult | null;
  /** Live distance from the projected winner to the line; `null` when nothing is projected. */
  readonly gap: number | null;
}

/** One market-detail clock → consumers, pipeline stage, and first-touch ETA. */
export function marketResolutionView(input: MarketResolutionViewInput): MarketResolutionView {
  const consumers = marketResolutionThresholdConsumers(
    input.resolutionThreshold,
    input.showGoalPercent === undefined ? {} : { showGoalPercent: input.showGoalPercent },
  );
  const pipelineStage = computeMarketPipelineStage(
    input.status,
    selectConvictionLeader(input.liveConviction)?.conviction ?? null,
    consumers.resolutionCardThreshold,
  );
  const projected = projectMarketResolutionAt({
    pipelineStage,
    config: input.config,
    outcomeCount: input.outcomeCount,
    tradingStartedAt: input.tradingStartedAt,
    convictionUpdatedAt: input.convictionUpdatedAt,
    retentionPerSecondPpb: input.retentionPerSecondPpb,
    outcomes: input.outcomes,
  });
  const threshold = consumers.resolutionCardThreshold;
  return {
    consumers,
    pipelineStage,
    projected,
    gap:
      projected === null || threshold === null
        ? null
        : projectedResolutionGap(threshold, input.liveConviction.get(projected.outcomeIndex)),
  };
}
